"use strict";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FeatureFlags } from "features/gating/flags";
import { useFeatureFlag } from "features/gating/hooks";
import { selectTransactionSettings } from "features/transactions/settings/selectors";
import {
  setTransactionSettings
} from "features/transactions/settings/slice";
import { logContextUpdate } from "utilities/src/logger/contextEnhancer";
export const TransactionSettingsContext = createContext(void 0);
export function TransactionSettingsContextProvider({
  settingKey,
  children,
  autoSlippageTolerance
}) {
  const appDispatch = useDispatch();
  const transactionSettings = useSelector(selectTransactionSettings(settingKey));
  const datadogEnabled = useFeatureFlag(FeatureFlags.Datadog);
  const v4HooksToggleFFEnabled = useFeatureFlag(FeatureFlags.SwapSettingsV4HooksToggle);
  const updateTransactionSettings = useCallback(
    (newState) => {
      appDispatch(setTransactionSettings({ settingKey, ...newState }));
    },
    [settingKey, appDispatch]
  );
  useEffect(() => {
    logContextUpdate("TransactionSettingsContext", transactionSettings, datadogEnabled);
  }, [transactionSettings, datadogEnabled]);
  useEffect(() => {
    updateTransactionSettings({ autoSlippageTolerance });
  }, [autoSlippageTolerance, updateTransactionSettings]);
  const state = useMemo(
    () => ({
      ...transactionSettings,
      updateTransactionSettings,
      selectedProtocols: transactionSettings.selectedProtocols,
      isV4HookPoolsEnabled: v4HooksToggleFFEnabled ? transactionSettings.isV4HookPoolsEnabled : false
    }),
    [transactionSettings, updateTransactionSettings, v4HooksToggleFFEnabled]
  );
  return <TransactionSettingsContext.Provider value={state}>{children}</TransactionSettingsContext.Provider>;
}
export const useTransactionSettingsContext = () => {
  const swapContext = useContext(TransactionSettingsContext);
  if (swapContext === void 0) {
    throw new Error("`useTransactionSettingsContext` must be used inside of `TransactionSettingsContextProvider`");
  }
  return swapContext;
};
